import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from 'antd';
import { EyeOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const FloatingButton = styled.button`
  position: fixed !important;
  bottom: 16px !important;
  right: 16px !important;
  width: 52px !important;
  height: 52px !important;
  border-radius: 50% !important;
  background: var(--brand-gold, #D4B254) !important;
  color: #1A1A1A !important;
  border: none !important;
  box-shadow: 0 4px 16px rgba(212, 178, 84, 0.4), 0 2px 8px rgba(0, 0, 0, 0.3) !important;
  cursor: pointer !important;
  display: none;
  align-items: center !important;
  justify-content: center !important;
  font-size: 22px !important;
  z-index: 500 !important;
  transition: transform 0.2s ease !important;

  &:active { transform: scale(0.95) !important; }

  @media (max-width: 1024px) {
    display: flex !important;
  }
`;

const StyledModal = styled(Modal)`
  max-width: 100vw !important;

  .ant-modal-content {
    background: #0a0a0a !important;
    padding: 0 !important;
    border-radius: 0 !important;
    border: none !important;
    height: 100vh !important;
    overflow: hidden !important;
  }

  .ant-modal-body {
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    height: 100vh !important;
    background: #0a0a0a !important;
  }

  .ant-modal-close {
    top: 12px !important;
    right: 12px !important;
    z-index: 10 !important;
  }

  .ant-modal-close-x {
    width: 48px !important;
    height: 48px !important;
    font-size: 18px !important;
    background: rgba(15, 14, 12, 0.9) !important;
    border: 1px solid var(--brand-gold, #D4B254) !important;
    border-radius: 50% !important;
    color: var(--brand-gold, #D4B254) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
`;

export const MobilePreviewModal: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasCanvas, setHasCanvas] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);
  // Keep a ref to the canvas's original parent so we can return it
  const originalParentRef = useRef<Element | null>(null);
  const originalNextSiblingRef = useRef<Node | null>(null);

  const moveCanvasToModal = useCallback(() => {
    const canvas = document.getElementById('preview-canvas') as HTMLCanvasElement | null;
    const slot = slotRef.current;
    if (!canvas || !slot) return false;

    // Remember where it came from
    originalParentRef.current = canvas.parentElement;
    originalNextSiblingRef.current = canvas.nextSibling;

    // Style it for the modal
    canvas.style.display = 'block';
    canvas.style.visibility = 'visible';
    canvas.style.opacity = '1';
    canvas.style.position = 'relative';
    canvas.style.maxWidth = '92vw';
    canvas.style.maxHeight = '85vh';
    canvas.style.width = 'auto';
    canvas.style.height = 'auto';
    canvas.style.borderRadius = '8px';
    canvas.style.boxShadow = '0 10px 40px rgba(0,0,0,0.6)';

    slot.appendChild(canvas);
    return true;
  }, []);

  const returnCanvas = useCallback(() => {
    const canvas = document.getElementById('preview-canvas') as HTMLCanvasElement | null;
    if (!canvas) return;

    // Reset styles
    canvas.style.display = '';
    canvas.style.visibility = '';
    canvas.style.opacity = '';
    canvas.style.position = '';
    canvas.style.maxWidth = '';
    canvas.style.maxHeight = '';
    canvas.style.width = '';
    canvas.style.height = '';
    canvas.style.borderRadius = '';
    canvas.style.boxShadow = '';

    // Return to original position
    const parent = originalParentRef.current;
    const nextSibling = originalNextSiblingRef.current;
    if (parent) {
      if (nextSibling && parent.contains(nextSibling)) {
        parent.insertBefore(canvas, nextSibling);
      } else {
        parent.appendChild(canvas);
      }
    }
    originalParentRef.current = null;
    originalNextSiblingRef.current = null;
  }, []);

  const openModal = useCallback(() => {
    setIsVisible(true);
  }, []);

  const closeModal = useCallback(() => {
    returnCanvas();
    setIsVisible(false);
    setHasCanvas(false);
  }, [returnCanvas]);

  // When modal opens, move the canvas in after it mounts
  useEffect(() => {
    if (!isVisible) return;

    // Wait for modal DOM to mount
    const timer = setTimeout(() => {
      const ok = moveCanvasToModal();
      setHasCanvas(ok);
    }, 80);

    return () => clearTimeout(timer);
  }, [isVisible, moveCanvasToModal]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      returnCanvas();
    };
  }, [returnCanvas]);

  return (
    <>
      <FloatingButton onClick={openModal} title="Preview Card" aria-label="Preview Card">
        <EyeOutlined />
      </FloatingButton>

      <StyledModal
        open={isVisible}
        onCancel={closeModal}
        footer={null}
        width="100%"
        className="mobile-preview-modal"
        style={{ top: 0, maxWidth: '100vw', paddingBottom: 0, margin: 0 }}
        bodyStyle={{ height: '100vh', padding: 0 }}
        closeIcon={<CloseOutlined />}
        destroyOnClose={false}
        maskStyle={{ background: 'rgba(0,0,0,0.97)' }}
        centered={false}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            boxSizing: 'border-box',
          }}
        >
          {/* Canvas gets moved here by moveCanvasToModal() */}
          <div ref={slotRef} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} />

          {!hasCanvas && (
            <div style={{
              color: 'rgba(255,255,255,0.6)',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              textAlign: 'center',
              position: 'absolute',
            }}>
              Loading preview…
            </div>
          )}
        </div>
      </StyledModal>
    </>
  );
};
